import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function AttAddUpdateRecord({
  isUpdateMode,
  userAttendanceLogs,
  updateData,
  showModal,
  setIsUpdateMode,
  setUpdateData,
  refreshFunc,
  userData,
}) {
  const dispatch = useDispatch();
  const params = useParams();

  /*   All States
   ********************************************* */

  const isNightOfficeUser = Boolean(userData?.isNightOffice);
  const inputFields = isNightOfficeUser
    ? [
        { name: "Date", value: "date", type: "date" },
        { name: "Record Time", value: "time", type: "time" },
      ]
    : [
        { name: "Date", value: "date", type: "date" },
        { name: "In-Time", value: "inTime", type: "time" },
        { name: "Out-Time", value: "outTime", type: "time" },
      ];

  const [formData, setFormData] = useState(null);

  const [loading, setLoading] = useState(false);

  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  /*   All Functions
   ********************************************* */

  const getUtcIsoFromDateTime = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) {
      return null;
    }
    const [year, month, day] = dateValue.split("-").map(Number);
    const [hours, minutes] = timeValue.split(":").map(Number);

    return new Date(
      Date.UTC(year, month - 1, day, hours, minutes)
    ).toISOString();
  };

  const addRecord = async () => {
    if (!formData?.date || !formData?.inTime || !formData?.outTime) {
      dispatch(
        setNotification({
          message: "Please provide date, in-time and out-time",
          type: "error",
        })
      );
      return;
    }

    const finalInDateString = getUtcIsoFromDateTime(
      formData.date,
      formData.inTime
    );

    const finalOutDate = new Date(finalInDateString);
    const [outHours, outMinutes] = formData.outTime.split(":").map(Number);
    if (isNightOfficeUser) {
      finalOutDate.setUTCDate(finalOutDate.getUTCDate() + 1);
    }
    finalOutDate.setUTCHours(outHours, outMinutes, 0, 0);
    const finalOutDateString = finalOutDate.toISOString();

    const requestBody = {
      inTime: finalInDateString,
      outTime: finalOutDateString,
      deviceId: params.deviceId,
      userId: params.userId,
    };

    try {
      loadingOn();
      const { data } = await API.post("/attendance-logs", requestBody, {
        headers: authHeader(),
      });
      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        const newDate = new Date(formData.date);
        newDate.setDate(newDate.getDate() + 1);
        await refreshFunc();
        setFormData((prev) => ({
          ...(prev || {}),
          date: newDate.toISOString().split("T")[0],
          inTime: "00:00",
          outTime: "12:00",
        }));
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const addNightRecord = async () => {
    if (!formData?.date || !formData?.time) {
      dispatch(
        setNotification({
          message: "Please provide date and time",
          type: "error",
        })
      );
      return;
    }

    const recordTime = getUtcIsoFromDateTime(formData.date, formData.time);
    if (!recordTime) {
      return;
    }

    const requestBody = {
      recordTime,
      deviceId: params.deviceId,
      userId: params.userId,
    };

    try {
      loadingOn();
      const { data } = await API.post(
        "/attendance-logs-night-user",
        requestBody,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        await refreshFunc();
        setFormData((prev) => ({
          ...(prev || {}),
          time: "",
        }));
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const updateRecord = async () => {
    if (!formData?.date || !formData?.inTime || !formData?.outTime) {
      dispatch(
        setNotification({
          message: "Please provide date, in-time and out-time",
          type: "error",
        })
      );
      return;
    }

    const finalInDateString = getUtcIsoFromDateTime(
      formData.date,
      formData.inTime
    );
    const finalOutDateString = getUtcIsoFromDateTime(
      formData.date,
      formData.outTime
    );

    const requestBody = {
      inTime: finalInDateString,
      outTime: finalOutDateString,
      deviceId: params.deviceId,
      userId: params.userId,
      inTimeRecordId: formData.inTimeRecordId,
      outTimeRecordId: formData.outTimeRecordId,
    };

    try {
      loadingOn();
      const { data } = await API.patch("/attendance-logs", requestBody, {
        headers: authHeader(),
      });
      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        await refreshFunc();
        resetData();
        showModal(false);
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const updateNightRecord = async () => {
    if (!formData?.recordId || !formData?.date || !formData?.time) {
      dispatch(
        setNotification({
          message: "Please provide date and time",
          type: "error",
        })
      );
      return;
    }

    const recordTime = getUtcIsoFromDateTime(formData.date, formData.time);
    if (!recordTime) {
      return;
    }

    const requestBody = {
      recordTime,
      deviceId: params.deviceId,
      userId: params.userId,
      recordId: formData.recordId,
    };

    try {
      loadingOn();
      const { data } = await API.patch(
        "/attendance-logs-night-user",
        requestBody,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        await refreshFunc();
        resetData();
        showModal(false);
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...(prev || {}),
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isNightOfficeUser) {
      return isUpdateMode ? updateNightRecord() : addNightRecord();
    }
    return isUpdateMode ? updateRecord() : addRecord();
  };

  function resetData() {
    setIsUpdateMode(false);
    setUpdateData(null);
    setFormData(null);
  }

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    if (!isUpdateMode || !updateData) {
      return;
    }

    if (!isNightOfficeUser) {
      const date = new Date(updateData.recordTime);
      const filteredRecords = userAttendanceLogs.filter(
        (record) =>
          new Date(record.recordTime).toISOString().split("T")[0] ===
          date.toISOString().split("T")[0]
      );

      const inTime =
        filteredRecords.length > 0
          ? filteredRecords[0].recordTime
          : updateData.recordTime;
      const outTime =
        filteredRecords.length > 1
          ? filteredRecords[filteredRecords.length - 1].recordTime
          : inTime;

      const inTimeDate = new Date(inTime);
      const outTimeDate = new Date(outTime);
      const inHours = String(inTimeDate.getUTCHours()).padStart(2, "0");
      const inMinutes = String(inTimeDate.getUTCMinutes()).padStart(2, "0");
      const outHours = String(outTimeDate.getUTCHours()).padStart(2, "0");
      const outMinutes = String(outTimeDate.getUTCMinutes()).padStart(2, "0");

      const formattedDate = date.toISOString().split("T")[0];
      setFormData({
        date: formattedDate,
        inTime: `${inHours}:${inMinutes}`,
        inTimeRecordId:
          filteredRecords.length > 0 ? filteredRecords[0].id : null,
        outTimeRecordId:
          filteredRecords.length > 1
            ? filteredRecords[filteredRecords.length - 1].id
            : null,
        outTime: `${outHours}:${outMinutes}`,
      });
      return;
    }

    const recordDate = new Date(updateData.recordTime);
    const formattedDate = recordDate.toISOString().split("T")[0];
    const formattedTime = recordDate
      .toISOString()
      .split("T")[1]
      .substring(0, 5);
    const recordType = recordDate.getUTCHours() >= 12 ? "in" : "out";

    setFormData({
      date: formattedDate,
      time: formattedTime,
      recordId: updateData.id,
      recordType,
    });
  }, [isNightOfficeUser, isUpdateMode, updateData, userAttendanceLogs]);
  return (
    <div>
      <h3>{isUpdateMode ? "Update Record" : "Add Record"}</h3>
      {loading && <Spinner />}
      <form onSubmit={handleSubmit} className="mt-3">
        {/*   Input Fields
         ********************************************* */}
        {inputFields.map((item, index) => (
          <div className="mb-2" key={index}>
            <label htmlFor={item.value} className="form-label">
              {item.name}
            </label>
            {/* <span className="ms-2">{JSON.stringify(formData)}</span> */}
            <input
              type={item.type}
              className="form-control"
              id={item.value}
              name={item.value}
              onChange={handleChange}
              value={formData?.[item.value] || ""}
              required
            />
          </div>
        ))}

        {/*   Buttons
         ********************************************* */}
        <div className="text-end">
          <button
            className="btn btn-sm btn-secondary me-1"
            type="button"
            onClick={() => {
              resetData();
              showModal(false);
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-success"
            type="submit"
            disabled={loading}
          >
            {isUpdateMode ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}
